import { Currency } from "@inrev/common";
import { DateTime } from "luxon";

const MS_PER_DAY = 86_400_000;

const DEFAULT_WINDOW_DAYS = 30;
const DEFAULT_SERIES_BUCKETS = 12;

export type MetricTrend = {
	/** Bucketed inflow driving the sparkline, oldest → newest. */
	series: number[];
	current: number;
	prior: number;
};

export type TrendEvent = { atMs: number; weight: number };

export type BuildTrendOptions = {
	/** Length of the current (and prior) window in days. */
	windowDays?: number;
	/** Number of sparkline buckets across the current window. */
	buckets?: number;
	/** Accumulate dollar weights via `Currency.add` to avoid float drift. */
	currency?: boolean;
};

// Parse ISO strings anchored to UTC. Date-only values (`ISODate`) are serialized as the
// UTC calendar date (`dt.toUTC().toISODate()`), so they must be read back in UTC — parsing
// them in the client's local zone re-anchors a bare date to local midnight, which for
// clients behind UTC lands after "now" and makes a same-day event look future-dated (and
// drop out of trailing windows). Full `ISODateTime` values carry an explicit offset, so the
// UTC default is ignored and their absolute instant is preserved.
export const toMs = (value: string | DateTime): number =>
	typeof value === "string"
		? DateTime.fromISO(value, { zone: "utc" }).toMillis()
		: value.toMillis();

// `currency` weights are dollar amounts and must accumulate via Currency.add to
// avoid float drift; count weights use plain `+`.
export const buildTrend = (
	events: TrendEvent[],
	nowMs: number,
	options?: BuildTrendOptions,
): MetricTrend => {
	const {
		windowDays = DEFAULT_WINDOW_DAYS,
		buckets = DEFAULT_SERIES_BUCKETS,
		currency = false,
	} = options ?? {};
	const windowMs = windowDays * MS_PER_DAY;
	const windowStartMs = nowMs - windowMs;
	const priorStartMs = nowMs - windowMs * 2;
	const bucketMs = windowMs / buckets;
	const add = currency ? Currency.add : (a: number, b: number) => a + b;

	const series = new Array<number>(buckets).fill(0);
	let current = 0;
	let prior = 0;

	for (const { atMs, weight } of events) {
		if (atMs >= windowStartMs && atMs <= nowMs) {
			current = add(current, weight);
			const bucket = Math.min(
				buckets - 1,
				Math.max(0, Math.floor((atMs - windowStartMs) / bucketMs)),
			);
			series[bucket] = add(series[bucket], weight);
		} else if (atMs >= priorStartMs && atMs < windowStartMs) {
			prior = add(prior, weight);
		}
	}

	return { series, current, prior };
};
